// react
import { useCallback, useEffect, useState } from 'react';

// types
import { ConditionPayload, Condition } from '../@types';

// services
import {
	createCondition,
	deactivateCondition as deactivateConditionService,
	fetchCondition,
	fetchConditions,
	updateCondition,
	ApiResponse,
	QueryParams,
} from '../services/api';

// Types

export interface UseConditionsParams {
	activityId?: number;
	contractId?: number;
	workPlanId?: number;
	isEnabled?: boolean;
	projectId?: number;
	query?: QueryParams;
}

export interface UseConditionsReturn {
	create: (payload: ConditionPayload) => Promise<ApiResponse<Condition>>;
	data: Condition[];
	deactivate: (id: number) => Promise<ApiResponse<Condition>>;
	error: string | null;
	loading: boolean;
	pagination: ApiResponse<Condition[]>['pagination'];
	refetch: () => Promise<void>;
	update: (id: number, payload: Partial<ConditionPayload>) => Promise<ApiResponse<Condition>>;
}

// Hook

export const useConditions = ({
	activityId,
	contractId,
	workPlanId,
	isEnabled = true,
	projectId,
	query = {},
}: UseConditionsParams = {}): UseConditionsReturn => {
	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [data, setData] = useState<Condition[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState<ApiResponse<Condition[]>['pagination']>(undefined);

	// fetch data
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const queryKey = JSON.stringify(query);

	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const res = await fetchConditions(
				{ accessToken, idToken },
				{
					...query,
					activity: activityId,
					contract: contractId,
					workPlan: workPlanId,
					project: projectId,
				}
			);

			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || []);
				setPagination(res.pagination);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch conditions');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, activityId, contractId, workPlanId, projectId, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && projectId) {
			fetchData();
		}
	}, [isEnabled, fetchData, projectId]);

	// create condition
	const create = useCallback(
		async (payload: ConditionPayload): Promise<ApiResponse<Condition>> => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await createCondition({ accessToken, idToken }, payload);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to create condition';
				return { error: { id: 'CREATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData]
	);

	// update condition
	const update = useCallback(
		async (id: number, payload: Partial<ConditionPayload>): Promise<ApiResponse<Condition>> => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await updateCondition({ accessToken, idToken }, id, payload);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to update condition';
				return { error: { id: 'UPDATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData]
	);

	// deactivate condition (soft-delete via PATCH status='DEACTIVATED')
	const deactivate = useCallback(
		async (id: number): Promise<ApiResponse<Condition>> => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await deactivateConditionService({ accessToken, idToken }, id);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to deactivate condition';
				return { error: { id: 'DEACTIVATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData]
	);

	return {
		create,
		data,
		deactivate,
		error,
		loading,
		pagination,
		refetch: fetchData,
		update,
	};
};

// Single Condition Hook

export interface UseConditionParams {
	id: number;
	isEnabled?: boolean;
	query?: QueryParams;
}

export interface UseConditionReturn {
	data: Condition | null;
	error: string | null;
	loading: boolean;
	refetch: () => Promise<void>;
	update: (payload: Partial<ConditionPayload>) => Promise<ApiResponse<Condition>>;
}

export const useCondition = ({
	id,
	isEnabled = true,
	query = {},
}: UseConditionParams): UseConditionReturn => {
	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [data, setData] = useState<Condition | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	// fetch data
	const queryKey = JSON.stringify(query);

	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken || !id) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const res = await fetchCondition({ accessToken, idToken }, id, query);

			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || null);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch condition');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, id, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && id) {
			fetchData();
		}
	}, [isEnabled, fetchData, id]);

	// update condition
	const update = useCallback(
		async (payload: Partial<ConditionPayload>) => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			const res = await updateCondition({ accessToken, idToken }, id, payload);

			if (!res.error) {
				await fetchData();
			}

			return res;
		},
		[accessToken, idToken, id, fetchData]
	);

	return {
		data,
		error,
		loading,
		refetch: fetchData,
		update,
	};
};
